/**
 * Cashier multi-tenant auth client (Phase 8.1).
 *
 * Standalone module — independent of @matix/auth (which stays single-step
 * for admin/kds backward compat). Stores a stage-aware payload in
 * localStorage under `pos:cashier:mt-auth`.
 *
 * Stages: pending (after login) → branch-chosen → drawer-chosen → session.
 * Only the final 'session' stage is restored from storage on page reload —
 * pending stages have a 5-minute server TTL, not worth resuming.
 */

// Exported so the phone shell's cold-start biometric lock points at the
// SAME key rather than duplicating the string (packages/ui native-auth).
export const STORAGE_KEY = 'pos:cashier:mt-auth';

export type AuthStage =
  | 'pending'
  | 'subsidiary-chosen'
  | 'branch-chosen'
  | 'session';

export interface AuthUser {
  id: number;
  username: string;
  name: string;
  name_ar: string | null;
  tenant_slug: string;
}

export interface AuthSubsidiary {
  id: number;
  code: string;
  name: string;
}

export interface AuthBranch {
  id: number;
  code: string;
  name: string;
  subsidiary_id: number;
  /**
   * A till can be opened at this branch — either the branch has a cash drawer
   * of its own, or the signed-in user carries one on their profile
   * (`Security_Users.CashDrawerId`) in this company, which the server hands
   * out as the session's drawer. False only when there is no drawer at all,
   * and then the branch cannot be selected.
   */
  has_cash_drawer: boolean;
  /**
   * The branch HAS a drawer but it is switched off (mstr_CashDrawer.IsActive
   * = 0). Absent on an older API, which reads as false.
   */
  drawer_inactive?: boolean;
}

export interface AuthDrawer {
  id: number;
  code: string;
  name: string;
  branch_id: number;
}

export interface AuthSessionInfo {
  user_id: number;
  username: string;
  tenant_db: string;
  tenant_slug: string;
  subsidiary_id: number;
  subsidiary_name: string;
  branch_id: number;
  branch_name: string;
  cash_drawer_id: number;
  cash_drawer_name: string;
}

export interface PendingAuthData {
  stage: 'pending';
  token: string;
  saved_at: number;
  user: AuthUser;
  available_subsidiaries: AuthSubsidiary[];
}

export interface SubsidiaryChosenAuthData {
  stage: 'subsidiary-chosen';
  token: string;
  saved_at: number;
  user: AuthUser;
  subsidiary: AuthSubsidiary;
  available_branches: AuthBranch[];
}

export interface BranchChosenAuthData {
  stage: 'branch-chosen';
  token: string;
  saved_at: number;
  user: AuthUser;
  subsidiary: AuthSubsidiary;
  branch: AuthBranch;
  available_drawers: AuthDrawer[];
}

export interface SessionAuthData {
  stage: 'session';
  token: string;
  saved_at: number;
  user: AuthUser;
  subsidiary: AuthSubsidiary;
  branch: AuthBranch;
  drawer: AuthDrawer;
  session: AuthSessionInfo;
}

export type AuthData =
  | PendingAuthData
  | SubsidiaryChosenAuthData
  | BranchChosenAuthData
  | SessionAuthData;

export function saveAuth(data: AuthData): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function clearAuth(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
}

export function readAuth(): AuthData | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isAuthData(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getAuthToken(): string | null {
  return readAuth()?.token ?? null;
}

export function isSessionReady(): boolean {
  return readAuth()?.stage === 'session';
}

/** Read the `exp` (seconds since epoch) out of a JWT without verifying it. */
function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    // `exp` is a plain number — no UTF-8 concerns, so atob→JSON is safe here.
    const claims = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
}

/**
 * True when a stored `session` token's JWT has already expired.
 *
 * Guards the reload path: restoring an expired session used to leave the app in
 * a dead state — the MT store said `session`, but @matix/auth's AuthProvider
 * read the expired token, cleared it, and reported unauthenticated, so
 * LegacyApp rendered `null` and #root stayed empty behind the loading splash
 * forever (only a cache/site-data clear recovered it). A token with no `exp`
 * claim is treated as non-expiring (matches @matix/auth's isExpired).
 */
export function isSessionExpired(): boolean {
  const data = readAuth();
  if (!data || data.stage !== 'session') return false;
  const exp = decodeJwtExp(data.token);
  if (exp === null) return false;
  return Date.now() / 1000 >= exp - 5; // 5s skew buffer
}

function isAuthData(v: unknown): v is AuthData {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  if (typeof o.token !== 'string' || o.token.length === 0) return false;
  if (typeof o.saved_at !== 'number') return false;
  return (
    o.stage === 'pending' ||
    o.stage === 'subsidiary-chosen' ||
    o.stage === 'branch-chosen' ||
    o.stage === 'session'
  );
}
